// The pane on the right of Steps 2 and 3 with ?edit=1: with nothing selected, what can be
// changed on the view, overrides first; with a value selected, its form, the platform's value
// beside each field, and a reset (docs/Y2026-Q4/design-amazon-overview-overrides.md).
import { type ReactNode, useMemo, useRef, useState } from "react";

import {
  NOTE_MOVED,
  usePutBuildingNote,
} from "../../api/hooks/useAmazonBuildingNotes";
import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { cardLine, onStepThree, programmeFor, STEP_THREE_SHOWS } from "./blockers";
import type { StoryStep } from "./blueprint";
import { CellCard, type Mark, Marked } from "./CellCard";
import { cardLeavesOut, cardTexts, namer, shownPart } from "./shownPart";
import { recordLine, shownCell } from "./cells";
import { LINE_NAMES, linkLines, otherValue, PLAN, withValue } from "./previewLinks";
import { day, formatNumeric, today } from "./dates";
import { type Target, useEdit } from "./editContext";
import {
  applyOverrides,
  askKey,
  buildingKey,
  cardKey,
  type ContractField,
  contractKey,
  drifted,
  isHidden,
  type Kind,
  kindOf,
  NEED_OPTIONS,
  needKey,
  NO_OVERRIDES,
  type Overrides,
  platformValue,
  shownValue,
  STATE_OPTIONS,
  stepKey,
} from "./overrides";
import { stepById } from "./pathSteps";
import {
  DEFAULT_BUFFER_DAYS,
  neededBy,
  programmeLinks,
  projectCod,
  schedule,
} from "./schedule";
import styles from "./amazonOverview.module.css";

/** A wording the view writes itself, offered in the pane with the text it writes. */
export interface Wording {
  key: string;
  label: string;
  pageText: string;
}

/** Where each step's state is read from, as the pane names it. */
const STEP_SOURCE: Record<string, string> = {
  gridapp: "Grid's status row: the first application on record",
  gridoffer:
    "Grid's tracked dates: the DNO's promised date, else the application plus 3 months",
  gridacc: "Grid's tracked dates: the offer's accept-by date",
  structural:
    "Building readiness's structural history: the last accepted complete report, else the latest complete one",
  signoff:
    "Building readiness's structural history: Amazon's acceptance or an operator's confirmation, else the requirements still to come",
  access0:
    "Building readiness's histories: the first site visit logged in any area, else the latest access request, refusal or booking",
  access2:
    "Building readiness's histories: the first site visit after the survey was accepted",
  survey:
    "Building readiness's survey history: InRange's or the client's acceptance, or an operator's confirmation, else the requirements still to come",
  roof: "Building readiness's roof history: the roof ready for PV, else the remediation's stage or the report relied on",
  roofcheck:
    "Building readiness's roof history: the client's acceptance of the report after installation",
  llpack:
    "Legal's landlord pack history: the first email sending the pack to Amazon",
  llsent:
    "Legal's landlord pack history: the first email sending it to the landlord's side",
  llfeedback:
    "Legal's landlord pack history: the first feedback or form from the landlord's side",
  lldpack: "Legal's detailed consent pack date",
  llsignoff:
    "Legal's landlord pack history: the first email showing the consent signed",
  pcg: "Legal's parent guarantee stage and signed date",
  inputs: "The Ready to build checks for Amazon's design inputs",
  design: "Design readiness's lifecycle",
  dr3: "Design readiness's lifecycle",
  install: "The platform site's status",
  cod: "The platform site's status",
  sppa: "Legal's tenant PPA stage",
};

interface Field {
  key: string;
  label: string;
  kind: Kind;
  /** The value the page has without an override: the platform's, or the text it writes. */
  platform: string;
  /** The same for any building, where one form writes to every building. */
  platformOf: (b: AmazonBuilding) => string;
  long?: boolean;
  /** The choices where they differ from the kind's own (``OPTIONS``). */
  options?: [string, string][];
  /** Ticking writes "true". */
  box?: string;
  unit?: string;
  hint?: string;
  /** Text the page writes from the facts: it moves daily, so its drift is not flagged. */
  wording?: boolean;
  /** When the page shows the field, said while it shows nothing: else the form works out
   * from the step's states which of them show it. */
  shows?: string;
  /** Whether the page shows the field on the building as the form would leave it; else the
   * preview decides (anything the field moves), and a form with none shows every field. */
  inView?: (x: AmazonBuilding) => boolean;
}

const OPTIONS: Partial<Record<Kind, [string, string][]>> = {
  state: STATE_OPTIONS,
  need: NEED_OPTIONS,
};

const normal = (f: Field, v: string): string => {
  const x = v.trim();
  if (f.kind === "number" && x !== "") return String(Number(x));
  return x;
};

const FieldInput = ({
  f,
  id,
  value,
  disabled,
  onChange,
}: {
  f: Field;
  id: string;
  value: string;
  /** Not on the page right now: nothing typed here would show. */
  disabled: boolean;
  onChange: (v: string) => void;
}) => {
  if (f.kind === "flag")
    return (
      <label className={styles.editCheck} htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          disabled={disabled}
          checked={value === "true"}
          onChange={(e) => onChange(String(e.target.checked))}
        />{" "}
        {f.box}
      </label>
    );
  const options = f.options ?? OPTIONS[f.kind];
  if (options)
    return (
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {!options.some(([o]) => o === value) && (
          <option value={value}>
            {value ? shownValue(f.kind, value) : "-"}
          </option>
        )}
        {options.map(([o, l]) => (
          <option key={o} value={o}>
            {l}
          </option>
        ))}
      </select>
    );
  if (f.long)
    return (
      <textarea
        id={id}
        rows={3}
        maxLength={300}
        value={value}
        disabled={disabled}
        placeholder={f.platform || "Leave empty for the page's wording"}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  return (
    <input
      id={id}
      type={
        f.kind === "date" ? "date" : f.kind === "number" ? "number" : "text"
      }
      min={f.kind === "number" ? 0 : undefined}
      step={f.kind === "number" ? "any" : undefined}
      maxLength={f.kind === "text" ? 300 : undefined}
      value={value}
      disabled={disabled}
      placeholder={
        f.kind === "text"
          ? f.platform || "Leave empty for the page's wording"
          : undefined
      }
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

/** Under a field: the preview lines it changes, by their letters. */
const Drives = ({ lines, mark }: { lines: string[]; mark: Mark }) =>
  lines.length ? (
    <span className={styles.editDrives}>
      {lines.map((k) => (
        <span key={k} className={styles.cardLetter}>
          {mark(k)?.letter}
        </span>
      ))}
      Changes {lines.map((k) => LINE_NAMES[k]).join(", ")}
    </span>
  ) : (
    <span className={styles.muted}>
      Moves the dates on Step 3's programme, not the lines above
    </span>
  );

/** What a form last read or wrote under one building's key: the version to send next. */
interface Held {
  text: string | null;
  version: number;
}

const heldKey = (pid: string, key: string) => `${pid}|${key}`;

/** A field no state of its step brings into view on its own. */
const NOWHERE = "It shows once the step's other values here make the page use it.";

/** "a", "a or b", "a, b or c". */
const listed = (xs: string[]): string =>
  xs.length > 1 ? `${xs.slice(0, -1).join(", ")} or ${xs[xs.length - 1]}` : xs[0];

/** A form's live preview: what the page shows for a set of overrides, as keyed lines to
 * compare and as the view to draw, its lines lettered by ``mark``. */
interface Preview {
  texts: (o: Overrides) => Map<string, string>;
  render: (o: Overrides, mark: Mark) => ReactNode;
}

/** A form over one building's values, or, for the portfolio guarantee, every building's. */
const Form = ({
  building,
  everyBuilding,
  eyebrow,
  title,
  source,
  fields,
  preview,
  after,
}: {
  building: AmazonBuilding;
  /** Where one value covers the portfolio: the buildings it can be written to at once. */
  everyBuilding: AmazonBuilding[] | null;
  eyebrow: string;
  title: string;
  source: string;
  fields: Field[];
  preview: Preview | null;
  after: ReactNode;
}) => {
  const edit = useEdit();
  const put = usePutBuildingNote();
  const projectId = building.project_id;
  const o = edit.overrides(projectId);
  // Every field opens on what the page shows. An untouched one is never saved, so a wording
  // left as the page's own keeps following the facts.
  const initial = () =>
    Object.fromEntries(
      fields.map((f) => [f.key, o.get(f.key)?.text ?? f.platform]),
    );
  const [drafts, setDrafts] = useState<Record<string, string>>(initial);
  // What the form opened on: a field is saved only when changed from it, and with the
  // version read then, so someone else's change in the meantime is refused, not overwritten.
  const [starts, setStarts] = useState<Record<string, string>>(initial);
  const held = useRef<Map<string, Held> | null>(null);
  if (held.current === null) {
    held.current = new Map();
    for (const x of everyBuilding ?? [building])
      for (const f of fields) {
        const note = edit.overrides(x.project_id).get(f.key);
        held.current.set(heldKey(x.project_id, f.key), {
          text: note?.text ?? null,
          version: note?.version ?? 0,
        });
      }
  }
  const [all, setAll] = useState(everyBuilding !== null);
  const letters = useRef(new Map<string, string>());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  /** Writes ``want`` (null clears) under ``f.key`` on ``x``; the reason it failed, if it did.
   * Whether anything needs writing is judged against the value as it is now; the version
   * sent is the one this form read, so a change made since is refused rather than lost. */
  const write = async (x: AmazonBuilding, f: Field, want: string | null) => {
    const k = heldKey(x.project_id, f.key);
    const live = edit.overrides(x.project_id).get(f.key);
    const was = held.current?.get(k) ?? {
      text: live?.text ?? null,
      version: live?.version ?? 0,
    };
    if (want === (live?.text ?? null) && was.version === (live?.version ?? 0))
      return null;
    try {
      const res = await put.mutateAsync({
        project_id: x.project_id,
        step_id: f.key,
        version: was.version,
        text: want,
        platform: f.platformOf(x),
      });
      held.current?.set(k, {
        text: res.note?.text ?? null,
        version: res.note?.version ?? 0,
      });
      return null;
    } catch (err) {
      // Refused because it moved: the page now shows the other change, so the next save
      // reads the version from that.
      if ((err as Error).message === NOTE_MOVED) held.current?.delete(k);
      return (err as Error).message || "Could not save";
    }
  };
  const want = (f: Field, x: AmazonBuilding, v: string) =>
    v === "" || v === f.platformOf(x) ? null : v;
  const others =
    all && everyBuilding
      ? everyBuilding.filter((x) => x.project_id !== projectId)
      : [];
  const save = async () => {
    const changed = fields.filter(
      (f) => normal(f, drafts[f.key] ?? "") !== normal(f, starts[f.key] ?? ""),
    );
    // Across the portfolio, every field this building holds a value for, so the others match.
    const shared = others.length
      ? fields.filter(
          (f) =>
            changed.includes(f) ||
            (held.current?.get(heldKey(projectId, f.key))?.text ?? null) !==
              null,
        )
      : [];
    if (!changed.length && !shared.length) {
      setMessage("Nothing to save.");
      return;
    }
    setSaving(true);
    setMessage("");
    const failed: string[] = [];
    const saved: string[] = [];
    for (const f of fields) {
      const v = normal(f, drafts[f.key] ?? "");
      const targets = [
        ...(changed.includes(f) ? [building] : []),
        ...(shared.includes(f) ? others : []),
      ];
      let ok = true;
      for (const x of targets) {
        const why = await write(x, f, want(f, x, v));
        if (why) {
          failed.push(why);
          if (x === building) ok = false;
        }
      }
      if (ok && changed.includes(f)) saved.push(f.key);
    }
    setStarts((s) => ({
      ...s,
      ...Object.fromEntries(saved.map((k) => [k, drafts[k] ?? ""])),
    }));
    setSaving(false);
    setMessage(
      failed.length
        ? `Not all saved: ${[...new Set(failed)].join("; ")}`
        : "Saved.",
    );
  };
  /** Clears ``f`` on this building, and on every building where the form covers them all. */
  const clear = async (f: Field): Promise<string[]> => {
    const failed: string[] = [];
    for (const x of [building, ...others]) {
      const why = await write(x, f, null);
      if (why) failed.push(why);
    }
    if (!failed.length) {
      setDrafts((d) => ({ ...d, [f.key]: f.platform }));
      setStarts((s) => ({ ...s, [f.key]: f.platform }));
    }
    return failed;
  };
  const edited = (f: Field) =>
    o.has(f.key) || normal(f, drafts[f.key] ?? "") !== normal(f, f.platform);
  /** ``targets`` back to the platform's values: what is written over is cleared, and what
   * is typed but not saved is dropped. */
  const reset = async (targets: Field[]) => {
    setSaving(true);
    setMessage("");
    const failed: string[] = [];
    for (const f of targets.filter((x) => o.has(x.key)))
      failed.push(...(await clear(f)));
    const back = Object.fromEntries(
      targets.filter((f) => !o.has(f.key)).map((f) => [f.key, f.platform]),
    );
    setDrafts((d) => ({ ...d, ...back }));
    setStarts((s) => ({ ...s, ...back }));
    setSaving(false);
    setMessage(
      failed.length
        ? `Not reset: ${[...new Set(failed)].join("; ")}`
        : targets.length > 1
          ? "Everything here reset to the platform's values."
          : "Reset to the platform's value.",
    );
  };
  const changed = fields.filter(edited);
  // The preview as a save would leave it, and, field by field, with that field changed: the
  // lines that move are the ones the field drives.
  const value = (f: Field) => normal(f, drafts[f.key] ?? "");
  const drafted = fields.reduce(
    (acc, f) => withValue(acc, projectId, f.key, want(f, building, value(f))),
    o,
  );
  const links =
    preview &&
    linkLines(
      preview.texts(drafted),
      preview.texts(o),
      new Map(
        fields.map((f) => [
          f.key,
          preview.texts(
            withValue(drafted, projectId, f.key, otherValue(f.kind, value(f))),
          ),
        ]),
      ),
      letters.current,
    );
  const unsaved = fields.some(
    (f) => value(f) !== normal(f, starts[f.key] ?? ""),
  );
  // Whether the page shows each field with the form's values, and when it would.
  const leaves = applyOverrides(edit.platform(projectId) ?? building, drafted);
  const inView = (f: Field): boolean =>
    f.inView ? f.inView(leaves) : links ? links.moves(f.key) : true;
  const stateField = fields.find((f) => f.kind === "state");
  // Whether ``f`` moves anything with ``under`` written over the form's values.
  const movesUnder = (f: Field, under: Overrides): boolean => {
    if (!preview) return false;
    const base = preview.texts(under);
    const tried = preview.texts(withValue(under, projectId, f.key, otherValue(f.kind, value(f))));
    return [...new Set([...base.keys(), ...tried.keys()])].some(
      (k) => base.get(k) !== tried.get(k),
    );
  };
  // The states of the step that show ``f``, each alone, else each with one other fact set.
  const whenShown = (f: Field): string => {
    if (f.shows) return f.shows;
    if (!preview || !stateField || f === stateField) return NOWHERE;
    const under = (s: string) =>
      withValue(drafted, projectId, stateField.key, want(stateField, building, s));
    const states = STATE_OPTIONS.filter(([s]) => movesUnder(f, under(s)));
    if (states.length)
      return `Shows while the step is ${listed(states.map(([, l]) => l.toLowerCase()))}.`;
    for (const g of fields) {
      if (g === f || g === stateField || g.wording || g.inView) continue;
      const set = (s: string) =>
        withValue(under(s), projectId, g.key, otherValue(g.kind, value(g)));
      const paired = STATE_OPTIONS.filter(([s]) => movesUnder(f, set(s)));
      if (paired.length)
        return `Shows while the step is ${listed(paired.map(([, l]) => l.toLowerCase()))}, with ${g.label} set.`;
    }
    return NOWHERE;
  };
  // Each field the page shows nothing of, with when it would: worked out again only when a
  // value in the form changes, as finding when takes many previews.
  const draftsKey = JSON.stringify(drafts);
  const hidden = useMemo(
    () => new Map(fields.filter((f) => !inView(f)).map((f) => [f.key, whenShown(f)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draftsKey, fields, o],
  );
  return (
    <div className={styles.paneWide}>
      <div className={styles.eyebrow}>{eyebrow}</div>
      <h3>{title}</h3>
      {source && <span className={styles.muted}>Read from: {source}</span>}
      {preview && links && (
        <div className={styles.editPreview}>
          <span className={styles.eyebrow}>
            Amazon will see{unsaved ? ", with your unsaved changes" : ""}
          </span>
          {preview.render(drafted, links.mark)}
          {unsaved && (
            <span className={styles.muted}>
              Highlighted: what your unsaved changes alter.
            </span>
          )}
        </div>
      )}
      {fields.map((f, i) => {
        const note = o.get(f.key);
        const id = `edit-field-${i}`;
        const shown = !hidden.has(f.key);
        return (
          <div key={f.key} className={styles.editField}>
            <div className={styles.editFieldHead}>
              <label htmlFor={id}>
                {f.label}
                {f.unit ? ` (${f.unit})` : ""}
              </label>
              {edited(f) && (
                <button
                  type="button"
                  className={styles.linkButton}
                  disabled={saving}
                  onClick={() => void reset([f])}
                >
                  Reset to platform value
                </button>
              )}
            </div>
            <FieldInput
              f={f}
              id={id}
              value={drafts[f.key] ?? ""}
              disabled={!shown}
              onChange={(v) => setDrafts((d) => ({ ...d, [f.key]: v }))}
            />
            {shown ? (
              <span className={styles.muted}>
                {f.wording ? "The page says" : "Platform"}:{" "}
                {shownValue(f.kind, f.platform)}
              </span>
            ) : (
              <span className={styles.editHidden}>
                Not on the page right now, so this is switched off. {hidden.get(f.key)}
              </span>
            )}
            {f.hint && <span className={styles.muted}>{f.hint}</span>}
            {shown && links && <Drives lines={links.drives(f.key)} mark={links.mark} />}
            {note && (
              <span className={styles.editYours}>
                Yours{note.updated_by ? `, set by ${note.updated_by}` : ""} on{" "}
                {formatNumeric(day(note.updated_at))}
              </span>
            )}
            {!f.wording && drifted(note, f.platform) && (
              <span className={styles.error}>
                The platform has changed since: it showed{" "}
                {shownValue(f.kind, note?.platform ?? "")} when this was set.
              </span>
            )}
          </div>
        );
      })}
      {everyBuilding && (
        <label className={styles.editCheck} htmlFor="edit-every-building">
          <input
            id="edit-every-building"
            type="checkbox"
            checked={all}
            onChange={(e) => setAll(e.target.checked)}
          />{" "}
          Apply to every building: one guarantee covers the portfolio
        </label>
      )}
      <div className={styles.acts}>
        <button
          type="button"
          className={styles.buttonPrimary}
          disabled={saving}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {changed.length > 0 && (
          <button
            type="button"
            className={styles.button}
            disabled={saving}
            onClick={() => void reset(changed)}
          >
            Reset to platform values
          </button>
        )}
        <button
          type="button"
          className={styles.button}
          onClick={() => edit.select(null)}
        >
          Back to the list
        </button>
      </div>
      {message && (
        <span
          className={message.startsWith("Not ") ? styles.error : styles.muted}
        >
          {message}
        </span>
      )}
      {after}
    </div>
  );
};

const nameOf = (steps: StoryStep[], id: string): string =>
  steps.find((s) => s.id === id)?.title ?? stepById(id).title;

const FIELD_NAME: Record<string, string> = {
  state: "state",
  date: "date on record",
  due: "expected date",
  estimated: "estimate",
  hidden: "card hidden",
  kwp: "kWp",
  kw_ac: "kWac",
  cell: "Step 2 wording",
  line: "hover wording",
  source: "email cited",
  sent_by: "email sender",
  waiting_since: "waiting since",
  delivers: "document sent",
  finding: "survey finding",
  note: "state qualifier",
  cod_reason: "specialists' COD reason",
  site_code: "site code",
  verdict: "verdict",
  achievable_caption: "achievable size caption",
  committed_cod: "committed COD",
  achievable_cod: "earliest achievable COD",
  committed_kwp: "PPA kWp",
  committed_kw_ac: "PPA kWac",
  achievable_kwp: "achievable kWp",
  achievable_kw_ac: "achievable kWac",
  design: "Schedule 1 design submission",
  llsignoff: "Schedule 1 landlord approval",
  install: "Schedule 1 installation start",
  status: "status",
};

/** Building wording Step 3 writes itself, edited as wording rather than as a fact. */
const WRITTEN_BY_PAGE = new Set(["verdict", "achievable_caption"]);

/** An override's name in the list, and what opens it. */
const describe = (
  b: AmazonBuilding,
  key: string,
  steps: StoryStep[],
  wordings: Wording[],
): [string, Target] => {
  const [head, a, f] = key.split(".");
  const pid = b.project_id;
  if (head === "building") {
    const label = `${b.site_code} · ${FIELD_NAME[a]}`;
    if (!WRITTEN_BY_PAGE.has(a))
      return [label, { kind: "building", projectId: pid }];
    const w = wordings.find((x) => x.key === key);
    return [
      label,
      {
        kind: "wording",
        projectId: pid,
        key,
        label: w?.label ?? FIELD_NAME[a],
        pageText: w?.pageText ?? "(written on Step 3 from the facts)",
      },
    ];
  }
  if (head === "contract")
    return [
      `${b.site_code} · ${FIELD_NAME[a]}`,
      { kind: "contract", projectId: pid },
    ];
  if (head === "ask") {
    const ask = Object.values(b.asks ?? {})
      .flat()
      .find((x) => x.action_id === a);
    const title = ask?.title ?? "An open ask no longer on record";
    return [
      `${b.site_code} · open ask hidden: ${title}`,
      { kind: "ask", projectId: pid, actionId: a, title },
    ];
  }
  if (head === "need") {
    const need = b.needs?.find((n) => n.key === a);
    return [
      `${b.site_code} · ${need?.text ?? a}: ${FIELD_NAME[f]}`,
      { kind: "need", projectId: pid, key: a },
    ];
  }
  return [
    `${b.site_code} · ${nameOf(steps, head)}: ${a ? FIELD_NAME[a] : "Step 3 card line"}`,
    { kind: "step", projectId: pid, stepId: head },
  ];
};

const Row = ({
  label,
  value,
  target,
  tone,
}: {
  label: string;
  value: string;
  target: Target;
  tone?: string;
}) => {
  const edit = useEdit();
  return (
    <button
      type="button"
      className={styles.editRow}
      onClick={() => edit.select(target)}
    >
      <span>{label}</span>
      <span className={tone}>{value}</span>
    </button>
  );
};

export const EditPane = ({
  page,
  buildings,
  portfolio,
  steps,
  wordings,
}: {
  page: "achieved" | "site";
  /** The buildings the view shows, overrides applied: every one on Step 2, the open one on
   * Step 3. */
  buildings: AmazonBuilding[];
  /** Every building, overrides applied, for a value written across the portfolio. */
  portfolio: AmazonBuilding[];
  steps: StoryStep[];
  /** The view's own wordings for the open building (Step 3's verdict and caption). */
  wordings: Wording[];
}) => {
  const edit = useEdit();
  const sel = edit.selected;
  const find = (pid: string) => buildings.find((b) => b.project_id === pid);
  const b = sel && "projectId" in sel ? find(sel.projectId) : undefined;

  if (sel?.kind === "stepOne") {
    return (
      <div className={styles.paneWide}>
        <div className={styles.eyebrow}>Step 1 content</div>
        <h3>{nameOf(steps, sel.stepId)}</h3>
        <p className={styles.sub}>
          A step's name, owner, bullet points and needs belong to the shared
          blueprint. Edit them on Step 1 and Steps 2 and 3 follow.
        </p>
        <div className={styles.acts}>
          <button
            type="button"
            className={styles.buttonPrimary}
            onClick={() => edit.openStepOne(sel.stepId)}
          >
            Open on Step 1
          </button>
          <button
            type="button"
            className={styles.button}
            onClick={() => edit.select(null)}
          >
            Back to the list
          </button>
        </div>
      </div>
    );
  }

  if (sel?.kind === "computed") {
    return (
      <div className={styles.paneWide}>
        <div className={styles.eyebrow}>Worked out by the page</div>
        <h3>{sel.label}</h3>
        <p className={styles.sub}>
          The page works this out from the values below, so Steps 2 and 3 always
          agree. Change one of them and this follows.
        </p>
        <div className={styles.editGroup}>
          {sel.from.map((t, i) => {
            const fb = "projectId" in t ? find(t.projectId) : undefined;
            const label =
              t.kind === "step"
                ? `${fb?.site_code ?? ""} · ${nameOf(steps, t.stepId)}`
                : `${fb?.site_code ?? ""} · ${t.kind === "contract" ? "Schedule 1 dates" : "sizes and committed COD"}`;
            return <Row key={i} label={label} value="open" target={t} />;
          })}
        </div>
        <div className={styles.acts}>
          <button
            type="button"
            className={styles.button}
            onClick={() => edit.select(null)}
          >
            Back to the list
          </button>
        </div>
      </div>
    );
  }

  if (sel && b) {
    const factOf = (key: string) => (x: AmazonBuilding) =>
      platformValue(edit.platform(x.project_id) ?? x, key) ?? "";
    /** A fact field: the platform's value on this building, and on any other. */
    const fact = (
      key: string,
      rest: Omit<Field, "key" | "platform" | "platformOf">,
    ): Field => ({
      key,
      platform: factOf(key)(b),
      platformOf: factOf(key),
      ...rest,
    });
    const pid = b.project_id;
    const formKey = JSON.stringify(sel);
    if (sel.kind === "step") {
      const id = sel.stepId;
      const o = edit.overrides(pid);
      const step =
        programmeFor(b, steps).find((s) => s.id === id) ?? stepById(id);
      const named = namer(steps);
      // Each building's needed-by dates, as the page works them out, so a late input reads
      // late here too.
      const needOf = (x: AmazonBuilding) =>
        neededBy(x, programmeLinks(steps), DEFAULT_BUFFER_DAYS);
      // The platform's building: the preview applies the form's values to it afresh.
      const raw = edit.platform(pid) ?? b;
      const plat = (key: string) => factOf(key)(b);
      const stepOf = (x: AmazonBuilding) =>
        programmeFor(x, steps).find((st) => st.id === id) ?? stepById(id);
      const onCard = (x: AmazonBuilding) => onStepThree(x, steps, id);
      // Step 3's programme as the page plans it, compared but not drawn, so a date that only
      // moves the plan still reads as on the page.
      const plan = (x: AmazonBuilding): string =>
        JSON.stringify(
          Object.values(schedule(x, today(), programmeLinks(steps))).map((p) => [
            p.id,
            p.start?.getTime() ?? null,
            p.end?.getTime() ?? null,
            p.kind,
          ]),
        );
      // The Step 3 card's line, where Step 3 has a card for the step, marked when hidden.
      const card = (x: AmazonBuilding, ov: Overrides): string => {
        if (!onStepThree(x, steps, id)) return "";
        const text = ov.get(cardKey(id))?.text ?? cardLine(x, stepOf(x));
        return isHidden(ov, stepKey(id, "hidden")) ? `(hidden from readers) ${text}` : text;
      };
      const wording = (
        key: string,
        label: string,
        of: (x: AmazonBuilding) => string,
        long = false,
        hint?: string,
      ): Field => ({
        key,
        label,
        kind: "text",
        long,
        hint,
        wording: true,
        platform: of(b),
        platformOf: of,
      });
      const fields: Field[] = [
        fact(stepKey(id, "state"), {
          label: "State",
          kind: "state",
          hint:
            id === "gridoffer"
              ? "Step 2 shows the offer as in hand once it is accepted, whatever this says."
              : undefined,
        }),
        fact(stepKey(id, "date"), {
          label: "Date on record (done, or when the ask went out)",
          kind: "date",
        }),
        fact(stepKey(id, "due"), { label: "Expected date", kind: "date" }),
        ...(id === "gridoffer"
          ? [
              fact(stepKey(id, "estimated"), {
                label: "Estimate",
                kind: "flag",
                box: "This date is our own estimate, not the DNO's",
              }),
            ]
          : []),
        ...(id === "gridoffer" || id === "gridapp"
          ? [
              fact(stepKey(id, "kw_ac"), {
                label:
                  id === "gridapp"
                    ? "Capacity applied for"
                    : "Capacity offered",
                kind: "number",
                unit: "kWac",
                hint:
                  id === "gridoffer"
                    ? "Step 3's achievable size, and the installation time it sets, read this as the grid limit."
                    : undefined,
              }),
            ]
          : []),
        wording(
          stepKey(id, "cell"),
          "Step 2 cell wording",
          (x) => shownCell(x, id, NO_OVERRIDES, needOf(x)).text,
        ),
        {
          ...wording(
            stepKey(id, "line"),
            "Hover card: extra line",
            (x) => recordLine(x, id),
            true,
            id === "gridoffer"
              ? "Yours replaces Grid's offer terms on the hover card."
              : undefined,
          ),
          ...(cardLeavesOut(id, "line")
            ? { shows: "Shows on the building's own page; this step's hover card leaves it out." }
            : {}),
        },
        // The email behind the date only ever shows as the hover card's proof line.
        ...(cardLeavesOut(id, "proof")
          ? []
          : [
              fact(stepKey(id, "source"), {
                label: "Email behind the date (its subject)",
                kind: "text",
                hint: "Step 2's hover card cites it as the proof of the send.",
              }),
              fact(stepKey(id, "sent_by"), { label: "Sent by", kind: "text" }),
            ]),
        // Fields a step only sometimes carries: shown where the step uses them, the platform
        // has one, or one is written over.
        ...(
          [
            [
              "waiting_since",
              "Waiting since (counts the days on Step 2)",
              "date",
              ["signoff", "llfeedback", "llsent", "gridoffer"],
              false,
              "Shows while the step is in progress.",
            ],
            [
              "delivers",
              "Document the send carried (e.g. P02)",
              "text",
              ["signoff", "llfeedback"],
              false,
              "Shows with a waiting-since date, while the step is in progress.",
            ],
            [
              "finding",
              "What the survey found behind the size",
              "text",
              ["structural"],
              true,
              "Shows with a size, until the detailed design is done.",
            ],
            [
              "note",
              "Qualifier shown beside the state",
              "text",
              ["structural", "pcg"],
              false,
              id === "pcg"
                ? "Shows once the guarantee is delivered."
                : "Shows while no size is recorded.",
            ],
          ] as const
        )
          .filter(
            ([f, , , on]) =>
              (on as readonly string[]).includes(id) ||
              plat(stepKey(id, f)) !== "" ||
              o.has(stepKey(id, f)),
          )
          .map(([f, label, kind, , long, shows]) => ({
            ...fact(stepKey(id, f), { label, kind, shows }),
            long,
          })),
        {
          ...wording(
            cardKey(id),
            "Step 3 card line",
            (x) => cardLine(x, stepOf(x)),
            true,
          ),
          inView: onCard,
          shows: `Step 3 has no card for it, so the card is hidden. ${STEP_THREE_SHOWS}`,
        },
        {
          key: stepKey(id, "hidden"),
          label: "Step 3 card",
          kind: "flag",
          platform: "false",
          platformOf: () => "false",
          box: "Hide this card from readers",
          inView: onCard,
          shows: `Step 3 has no card for it, so there is nothing to hide. ${STEP_THREE_SHOWS}`,
        },
      ];
      return (
        <Form
          key={formKey}
          building={b}
          everyBuilding={id === "pcg" ? portfolio : null}
          eyebrow={`${b.site_code} · ${page === "site" ? "Step 3" : "Step 2"}`}
          title={step.title}
          source={STEP_SOURCE[id] ?? "The agents' record for this step"}
          fields={fields}
          preview={{
            texts: (ov) => {
              const x = applyOverrides(raw, ov);
              const part = shownPart(x, ov, id, named, needOf(x));
              const lines = new Map<string, string>(cardTexts(part));
              lines.set("cell", part.cell.text);
              const shownCard = card(x, ov);
              if (shownCard) lines.set("card", shownCard);
              lines.set(PLAN, plan(x));
              return lines;
            },
            render: (ov, mark) => {
              const x = applyOverrides(raw, ov);
              const part = shownPart(x, ov, id, named, needOf(x));
              const shownCard = card(x, ov);
              return (
                <>
                  <Marked k="cell" mark={mark}>
                    <span>
                      Step 2 cell: <b>{part.cell.text || "(empty)"}</b>
                    </span>
                  </Marked>
                  <div className={styles.editCard}>
                    <CellCard b={x} parts={[part]} mark={mark} />
                  </div>
                  {shownCard && (
                    <Marked k="card" mark={mark}>
                      <span>Step 3 card: {shownCard}</span>
                    </Marked>
                  )}
                </>
              );
            },
          }}
          after={
            <p className={styles.muted}>
              Name, owner and bullet points:{" "}
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => edit.openStepOne(id)}
              >
                edit on Step 1
              </button>
            </p>
          }
        />
      );
    }
    if (sel.kind === "building") {
      const f = (
        field: Parameters<typeof buildingKey>[0],
        label: string,
        kind: Kind,
        unit?: string,
      ): Field => fact(buildingKey(field), { label, kind, unit });
      return (
        <Form
          key={formKey}
          building={b}
          everyBuilding={null}
          eyebrow={b.site_code}
          title="Building facts"
          source="Legal's and the specialists' attributes on the project"
          fields={[
            f("site_code", "Site code", "text"),
            f("committed_kwp", "PPA committed size", "number", "kWp"),
            f("committed_kw_ac", "PPA committed size", "number", "kWac"),
            {
              ...f("achievable_kwp", "Recorded achievable size", "number", "kWp"),
              hint: "Step 3's tile shows the lower of the structural and grid limits; a figure written here replaces that unit's on the tile.",
            },
            {
              ...f("achievable_kw_ac", "Recorded achievable size", "number", "kWac"),
              hint: "Step 3's tile shows the lower of the structural and grid limits; a figure written here replaces that unit's on the tile.",
            },
            f("committed_cod", "Committed COD", "date"),
            f("achievable_cod", "Specialists' earliest achievable COD", "date"),
            {
              ...f(
                "cod_reason",
                "The specialists' reason for that date",
                "text",
              ),
              long: true,
              inView: (x) =>
                projectCod(x, schedule(x, today(), programmeLinks(steps)).cod.end)
                  .bySpecialists,
              shows:
                "Shows under Step 3's projected COD while the specialists' achievable COD sets it, that is while their date is later than the page's own plan.",
            },
          ]}
          preview={null}
          after={
            <p className={styles.muted}>
              The committed COD moves the row order on Step 2. The sizes and
              both CODs move the projection, needed-by dates and verdict on Step
              3.
            </p>
          }
        />
      );
    }
    if (sel.kind === "contract") {
      const f = (field: ContractField, label: string): Field =>
        fact(contractKey(field), { label, kind: "date" });
      return (
        <Form
          key={formKey}
          building={b}
          everyBuilding={null}
          eyebrow={`${b.site_code} · Step 3`}
          title="Schedule 1 contract dates"
          source="Legal's Schedule 1 dates from the signed SPPA"
          fields={[
            f("design", "Detailed design submission due"),
            f(
              "llsignoff",
              b.freehold
                ? "Amazon Real Estate approval due"
                : "Landlord approval due",
            ),
            f("install", "Installation start due"),
          ]}
          preview={null}
          after={
            <p className={styles.muted}>
              Needed-by dates use the earlier of what holds the committed COD
              and these.
            </p>
          }
        />
      );
    }
    if (sel.kind === "need") {
      const need = b.needs?.find((n) => n.key === sel.key);
      return (
        <Form
          key={formKey}
          building={b}
          everyBuilding={null}
          eyebrow={`${b.site_code} · Needed from Amazon`}
          title={need?.text ?? sel.key}
          source="Its keeper's Ready to build check"
          fields={[
            fact(needKey(sel.key, "status"), {
              label: "Status",
              kind: "need",
              // Partially received is a grade's, so only a graded item offers it.
              options: need?.graded
                ? NEED_OPTIONS
                : NEED_OPTIONS.filter(([status]) => status !== "partial"),
            }),
            fact(needKey(sel.key, "date"), {
              label: "Received on",
              kind: "date",
              inView: (x) =>
                x.needs?.find((n) => n.key === sel.key)?.status === "received",
              shows: "Shows beside the item once its status is Received.",
            }),
          ]}
          preview={null}
          after={
            <p className={styles.muted}>
              Step 3 lists only what is still needed, so an item marked received
              leaves the table; this list keeps it.
            </p>
          }
        />
      );
    }
    if (sel.kind === "ask") {
      return (
        <Form
          key={formKey}
          building={b}
          everyBuilding={null}
          eyebrow={`${b.site_code} · Step 3 · open ask`}
          title={sel.title}
          source="The specialist's action list"
          fields={[
            {
              key: askKey(sel.actionId),
              label: "On the blocker card",
              kind: "flag",
              platform: "false",
              platformOf: () => "false",
              box: "Hide this ask from readers",
              inView: () => false,
              shows:
                "Step 3 does not list open asks at the moment, so hiding one changes nothing readers see.",
            },
          ]}
          preview={null}
          after={
            <p className={styles.muted}>
              The ask stays open on the specialist's list; this only hides it on
              this page.
            </p>
          }
        />
      );
    }
    if (sel.kind === "wording") {
      return (
        <Form
          key={formKey}
          building={b}
          everyBuilding={null}
          eyebrow={`${b.site_code} · Step 3`}
          title={sel.label}
          source="Written by the page from the facts"
          fields={[
            {
              key: sel.key,
              label: "Wording Amazon sees",
              kind: kindOf(sel.key),
              long: true,
              wording: true,
              platform: sel.pageText,
              platformOf: () => sel.pageText,
            },
          ]}
          preview={null}
          after={
            <p className={styles.muted}>
              Your wording does not follow the facts: if they move, this shows
              what the page would now say.
            </p>
          }
        />
      );
    }
  }

  // Nothing selected: what can be changed here, overrides first.
  const scope = buildings;
  const mine = scope.flatMap((x) =>
    [...edit.overrides(x.project_id).values()].map((note) => ({ b: x, note })),
  );
  const moved = (x: AmazonBuilding, key: string) => {
    const raw = edit.platform(x.project_id);
    const note = edit.overrides(x.project_id).get(key);
    return raw !== undefined && drifted(note, platformValue(raw, key));
  };
  const changed = mine.filter(({ b: x, note }) =>
    moved(x, note.step_id),
  ).length;
  const one = page === "site" ? scope[0] : undefined;
  return (
    <div className={styles.paneWide}>
      <div className={styles.eyebrow}>Edit mode</div>
      <h3>
        What you can change on {one ? `${one.site_code}, Step 3` : "Step 2"}
      </h3>
      <p className={styles.muted}>
        Changes show to everyone who opens the page; only ?edit=1 shows these
        controls and the marks. Click a value on the page, or one below.
      </p>
      {mine.length > 0 && (
        <div className={styles.editGroup}>
          <b>
            Your overrides ({mine.length}
            {changed ? `, ${changed} changed on the platform since` : ""})
          </b>
          {mine.map(({ b: x, note }) => {
            const [label, target] = describe(x, note.step_id, steps, wordings);
            return (
              <Row
                key={`${x.project_id}|${note.step_id}`}
                label={label}
                value={moved(x, note.step_id) ? "changed" : "set"}
                tone={moved(x, note.step_id) ? styles.error : styles.editYours}
                target={target}
              />
            );
          })}
        </div>
      )}
      {one ? (
        <>
          <div className={styles.editGroup}>
            <b>{one.site_code}</b>
            <Row
              label="Sizes, committed and earliest COD"
              value="facts"
              target={{ kind: "building", projectId: one.project_id }}
            />
            <Row
              label="Schedule 1 contract dates"
              value="3 dates"
              target={{ kind: "contract", projectId: one.project_id }}
            />
            {wordings.map((w) => (
              <Row
                key={w.key}
                label={w.label}
                value="wording"
                target={{ kind: "wording", projectId: one.project_id, ...w }}
              />
            ))}
          </div>
          <div className={styles.editGroup}>
            <b>Steps on {one.site_code}</b>
            {programmeFor(one, steps).map((s) => (
              <Row
                key={s.id}
                label={s.title}
                value={shownValue("state", one.steps[s.id]?.state ?? "")}
                target={{
                  kind: "step",
                  projectId: one.project_id,
                  stepId: s.id,
                }}
              />
            ))}
          </div>
          <div className={styles.editGroup}>
            <b>Needed from Amazon</b>
            {/* Only Amazon's list takes an override: an item added for the building is its
                keeper's check, changed on the specialist's page. */}
            {(one.needs ?? []).filter((n) => n.listed !== false).map((n) => (
              <Row
                key={n.key}
                label={n.text}
                value={shownValue("need", n.status)}
                target={{ kind: "need", projectId: one.project_id, key: n.key }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className={styles.editGroup}>
          <b>Buildings</b>
          {scope.map((x) => (
            <Row
              key={x.project_id}
              label={`${x.site_code}: sizes and committed COD`}
              value={
                x.committed_cod ? formatNumeric(day(x.committed_cod)) : "-"
              }
              target={{ kind: "building", projectId: x.project_id }}
            />
          ))}
          <p className={styles.muted}>
            Each cell is one step on one building: click it to change its state,
            dates, size, wording or hover text.
          </p>
        </div>
      )}
    </div>
  );
};

/** The view with the pane on its right, in edit mode only. */
export const EditLayout = ({
  pane,
  children,
}: {
  pane: ReactNode;
  children: ReactNode;
}) => {
  const edit = useEdit();
  if (!edit.editing) return <>{children}</>;
  return (
    <div className={styles.editBody}>
      <div className={styles.editMain}>{children}</div>
      <section className={styles.pane} aria-live="polite">
        {pane}
      </section>
    </div>
  );
};
